import * as React from 'react';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import {
    getItemModelCatalogId,
    getItemModelCurrency,
    getItemModelSellerId,
    getItemModelSellerName,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { getRegistrationBidLimit } from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import { getSellerPhoneNumber } from '@/redux/modules/sellerDetail';
import { Info } from '@liveauctioneers/hammer-ui-core/icons';
import { InlineButton } from '@liveauctioneers/hammer-ui-core/inlineButton';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { openSendSellerMessageModal } from '@/redux/modules/modal';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import styled from 'styled-components';
import Tooltip from '@liveauctioneers/caterwaul-components/lib/Tooltip/Tooltip';

type Props = { className?: string; itemId: number; onClick?: () => void };

const BidLimitTooltip = ({ itemId, onClick }: Props) => {
    const dispatch = useAppDispatch();
    const catalogId = useAppSelector((state) => getItemModelCatalogId(state, itemId));
    const currency = useAppSelector((state) => getItemModelCurrency(state, itemId));
    const sellerId = useAppSelector((state) => getItemModelSellerId(state, itemId));
    const sellerName = useAppSelector((state) => getItemModelSellerName(state, itemId));
    const sellerPhoneNumber = useAppSelector((state) => getSellerPhoneNumber(state, sellerId));
    const bidLimit = useAppSelector((state) => getRegistrationBidLimit(state, catalogId));

    const { formatMessage, formatNumber } = useIntl();

    const showSendSellerMessageModal = React.useCallback(() => {
        const messageDetails = {
            message: formatMessage(
                {
                    id: 'registration.sellerMessage.body',
                },
                {
                    bidLimit: formatNumber(bidLimit, {
                        currency,
                        maximumFractionDigits: 0,
                        minimumFractionDigits: 0,
                        style: 'currency',
                    }),
                }
            ),
        };
        const subject = formatMessage({
            id: 'registration.sellerMessage.subject',
        });

        dispatch(openSendSellerMessageModal(messageDetails, sellerId, sellerName, subject));
    }, [formatMessage, formatNumber, bidLimit, currency, dispatch, sellerId, sellerName]);

    const handleOnClick = React.useCallback(() => {
        if (onClick) {
            onClick();
        } else {
            showSendSellerMessageModal();
        }
    }, [onClick, showSendSellerMessageModal]);

    return (
        <Tooltip
            data-testid="test"
            trigger={
                <Info
                    size="sm"
                    style="fill"
                />
            }
        >
            <FormattedMessage
                id="registration.auction_status_request_more_limit"
                values={{
                    requestLink: (
                        <StyledInlineButton
                            key="bid-limit-tooltip-request-link"
                            onClick={handleOnClick}
                            size="sm"
                        >
                            <FormattedMessage id="registration.auction_status_request_text" />
                        </StyledInlineButton>
                    ),
                    sellerPhoneNumber: (
                        <Link
                            href={`tel:${sellerPhoneNumber}`}
                            key="bid-limit-tooltip-seller-phone-number"
                            size="sm"
                        >
                            {sellerPhoneNumber}
                        </Link>
                    ),
                }}
            />
        </Tooltip>
    );
};

export default BidLimitTooltip;

const StyledInlineButton = styled(InlineButton)`
    text-decoration: underline;
`;
